import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
} from '@material-ui/core';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpExceptionsManagePermission } from '@internal/ksdp-permissions-common';
import { ExceptionSettings } from '../types';

export const ExceptionSettingsComponent = () => {
  const [settings, setSettings] = useState<ExceptionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reopenWindowDays, setReopenWindowDays] = useState<number>(30);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  // UI-only gate; PUT /settings is enforced by the backend router.
  const { allowed: canManage } = usePermission({
    permission: ksdpExceptionsManagePermission,
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('exceptions');
      const response = await fetchApi.fetch(
        `${baseUrl}/settings/reopen-window`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.statusText}`);
      }
      const data = await response.json();
      setSettings(data);
      setReopenWindowDays(data.reopenWindowDays || 7);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  const saveSettings = async () => {
    if (reopenWindowDays < 1 || reopenWindowDays > 365) {
      setError(new Error('Reopen window must be between 1 and 365 days'));
      return;
    }

    try {
      setSaving(true);
      const baseUrl = await discoveryApi.getBaseUrl('exceptions');
      const response = await fetchApi.fetch(
        `${baseUrl}/settings/reopen-window`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reopenWindowDays }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      await fetchSettings(); // Refresh settings
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Exception Settings
        </Typography>

        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Reopen Window Length
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            Number of days an approved exception remains valid before requiring
            re-evaluation.
          </Typography>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            value={reopenWindowDays}
            onChange={e => setReopenWindowDays(Number(e.target.value))}
            inputProps={{ min: 1, max: 365 }}
            style={{ width: 120, marginRight: 16 }}
            disabled={!canManage}
          />
          {canManage && (
            <Button
              variant="contained"
              color="primary"
              onClick={saveSettings}
              disabled={
                saving || reopenWindowDays < 1 || reopenWindowDays > 365
              }
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>

        {settings && (
          <Typography variant="caption" color="textSecondary">
            Current setting: {settings.reopenWindowDays} days
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
